
// ... imports
import { firebaseService, cleanForFirestore, getCircularReplacer, getAuthHeaders } from './services/firebaseService';
import { API_BASE } from './apiConfig';
import { geminiService } from './services/geminiService';
import { communicationService } from './services/communicationService';
import { nudgeService } from './services/nudgeService';
import { notificationService } from './services/notificationService';
import {
  LayoutGrid, BarChart3, ShieldAlert, LogOut,
  User as UserIcon, Command, Search, Bell, X,
  Menu, CheckCircle2, Zap, ClipboardList, Sliders,
  ExternalLink, ShieldAlert as ShieldIcon, ShieldCheck, LogIn,
  ShieldCheck as VerifiedIcon, Activity, Clock, Shield as ShieldSec,
  ArrowUpRight, Users, Loader2
} from 'lucide-react';
import { EisenhowerMatrix } from './components/EisenhowerMatrix';
import { TaskModal } from './components/TaskModal';
import { AnalyticsDashboard } from './components/AnalyticsDashboard';
import { IntelPage } from './components/Admin/IntelPage';
import { AIAgentOverlay } from './components/AIAgentOverlay';
import { SettingsPage } from './components/SettingsPage';
import { LandingPage } from './components/LandingPage';
import { AuthPage } from './components/AuthPage';
import { TaskLogPage } from './components/TaskLogPage';
import { TrustCenter } from './components/Governance/TrustCenter';
import { ConfirmationProvider } from './components/ConfirmationModal';
import { ActionFlowProvider } from './components/ActionModal'; // NEW IMPORT
import { UpgradeModal } from './components/UpgradeModal';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { HashRouter, Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { Task, User, UserTier, UserRole, Quadrant, Category, Broadcast, UserNotification, TaskStatus, SubTask, AccountabilityPartner, SystemEvent } from './types';

// ... (safeJsonStringify and PartnerActionRelay component code remains exactly same)

function safeJsonStringify(obj: any): string {
  try {
    const sanitized = cleanForFirestore(obj);
    return JSON.stringify(sanitized, getCircularReplacer());
  } catch (e) {
    console.error("[Vanguard] Serializer Failure:", e);
    return "{}";
  }
}

const PartnerActionRelay: React.FC = () => {
  const location = useLocation();
  const [status, setStatus] = useState<'IDLE' | 'PROCESSING' | 'SUCCESS' | 'ERROR'>('IDLE');

  useEffect(() => {
    const processAction = async () => {
      const search = location.search || (location.hash.includes('?') ? '?' + location.hash.split('?')[1] : '');
      const params = new URLSearchParams(search);

      const action = params.get('action');
      const userId = params.get('uid');
      const taskId = params.get('tid');
      const partnerEmail = params.get('p');

      if (action === 'request_pulse' && userId && taskId && partnerEmail) {
        setStatus('PROCESSING');
        try {
          await firebaseService.createBroadcast({
            title: 'Tactical Status Request',
            message: `Accountability partner [${partnerEmail}] is requesting a status update on a core mission.`,
            targetGroup: 'INDIVIDUAL',
            targetUserId: userId,
            priority: 'CRITICAL',
            timestamp: Date.now(),
            senderName: 'Accountability Relay',
            ctaLabel: 'REPLY TO PARTNER',
            ctaUrl: `/?taskId=${taskId}`
          });

          const operator = await firebaseService.getUser(userId);
          const tasks = await firebaseService.getTasks(userId);
          const task = tasks.find(t => t.id === taskId);

          if (operator) {
            await communicationService.processEvent({
              to: operator.email,
              event: 'partner_request',
              context: {
                userName: operator.displayName,
                taskTitle: task?.title || 'Active Mission',
                quadrant: task?.currentQuadrant || 'Primary Grid',
                pulseRequestUrl: window.location.origin + `/#/?taskId=${taskId}`,
                appUrl: window.location.origin
              }
            });
          }

          setTimeout(() => setStatus('SUCCESS'), 1500);
        } catch (e) {
          console.error("Relay Protocol Error", e);
          setStatus('ERROR');
        }
      } else if (action) {
        setStatus('ERROR');
      }
    };
    processAction();
  }, [location]);

  return (
    <div className="fixed inset-0 z-[1000] bg-vanguard-bg flex items-center justify-center p-6 text-center">
      <div className="max-w-md w-full glass p-12 rounded-[3.5rem] border border-vanguard-border space-y-8 animate-in fade-in zoom-in-95 duration-500 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-50" />

        {status === 'PROCESSING' && (
          <div className="space-y-6">
            <div className="relative mx-auto w-24 h-24">
              <Activity className="animate-spin text-blue-600 absolute inset-0" size={96} strokeWidth={1} />
              <ShieldSec className="text-blue-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" size={32} />
            </div>
            <div className="space-y-2">
              <p className="text-[10px] font-black uppercase tracking-[0.4em] text-blue-600 animate-pulse">Establishing Secure Handshake...</p>
              <p className="text-[8px] font-mono text-vanguard-muted uppercase">Verifying Intel Shards</p>
            </div>
          </div>
        )}

        {status === 'SUCCESS' && (
          <div className="space-y-8">
            <div className="w-24 h-24 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto text-emerald-500 shadow-inner">
              <VerifiedIcon size={48} />
            </div>
            <div className="space-y-3">
              <h2 className="text-3xl font-black text-vanguard-text uppercase tracking-tighter">Relay Secured</h2>
              <p className="text-sm text-vanguard-muted font-medium leading-relaxed">
                Transmission complete. The operator has been notified via <span className="text-blue-600 font-bold">Real-Time Push</span> and their Tactical Command Hub is now flagging your request.
              </p>
            </div>
            <div className="pt-6 border-t border-vanguard-border flex flex-col items-center gap-2">
              <div className="flex items-center gap-2 px-3 py-1 bg-black/5 rounded-full border border-black/5">
                <ShieldCheck size={12} className="text-emerald-500" />
                <span className="text-[8px] font-mono text-vanguard-muted uppercase tracking_widest">Shard Registry Updated</span>
              </div>
              <p className="text-[7px] font-mono text-vanguard-muted opacity-30 uppercase">Node ID: RELAY_{Math.random().toString(36).substr(2, 8).toUpperCase()}</p>
            </div>
          </div>
        )}

        {(status === 'ERROR' || status === 'IDLE') && (
          <div className="space-y-8">
            <div className="w-24 h-24 bg-rose-500/10 rounded-full flex items-center justify-center mx-auto text-rose-500">
              <ShieldAlert size={48} />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-black text-vanguard-text uppercase">Relay Link Unstable</h2>
              <p className="text-sm text-vanguard-muted">The tactical handshake could not be verified. This may be due to an expired shard or malformed transmission parameters.</p>
            </div>
            <div className="pt-4">
              <Link to="/" className="text-[10px] font-black text-blue-600 uppercase tracking-widest hover:underline">Return to Primary Grid</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const NavLinks: React.FC<{ user: User | null; isPremium?: boolean }> = ({ user, isPremium }) => {
  const location = useLocation();
  const links = [
    { to: '/', label: 'Matrix', icon: LayoutGrid, premium: false },
    { to: '/registry', label: 'Registry', icon: ClipboardList, premium: false },
    { to: '/strategy', label: 'Strategy', icon: BarChart3, premium: true },
    { to: '/settings', label: 'Settings', icon: Sliders, premium: false },
    { to: '/trust', label: 'Trust Center', icon: ShieldCheck, premium: false },
  ];

  return (
    <div className="space-y-2">
      {links.map(link => {
        const isActive = location.pathname === link.to;
        const isLocked = link.premium && !isPremium;
        return (
          <Link
            key={link.to}
            to={link.to}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${isActive ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-vanguard-muted hover:bg-black/5 hover:text-vanguard-text'}`}
          >
            <link.icon size={18} />
            <span className="text-[10px] font-bold uppercase tracking-widest">{link.label}</span>
            {isLocked && <span className="ml-auto text-[8px] font-black text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded-full uppercase tracking-wider">Pro</span>}
          </Link>
        )
      })}
      {user?.role === UserRole.ADMIN && (
        <Link
          to="/admin"
          className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${location.pathname === '/admin' ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-vanguard-muted hover:bg-black/5 hover:text-vanguard-text'}`}
        >
          <ShieldAlert size={18} />
          <span className="text-[10px] font-bold uppercase tracking-widest">Command</span>
        </Link>
      )}
    </div>
  );
};

// ... (PromotionDialog and AppLayout components code remains exactly same, omitting for brevity until App component)

const PromotionDialog: React.FC<{
  isOpen: boolean;
  parentTask: Task | null;
  subtask: SubTask | null;
  onClose: () => void;
  onConfirm: (keepPartners: boolean) => void;
}> = ({ isOpen, parentTask, subtask, onClose, onConfirm }) => {
  const [keepPartners, setKeepPartners] = useState(true);
  const [isPromoting, setIsPromoting] = useState(false);

  if (!isOpen || !parentTask || !subtask) return null;

  const hasPartners = (parentTask.partners || []).length > 0;

  const handleConfirm = async () => {
    if (isPromoting) return; // Guard against double-clicks
    setIsPromoting(true);
    try {
      await onConfirm(keepPartners);
    } catch (e) {
      setIsPromoting(false); // Only reset on error so user can retry
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 space-y-6 animate-in zoom-in-95 duration-200 border border-slate-100">

        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
            <ArrowUpRight size={24} />
          </div>
          <div>
            <h3 className="text-lg font-black text-slate-900 tracking-tight">Promote Objective</h3>
            <p className="text-xs text-slate-500 font-medium">Verify execution parameters.</p>
          </div>
        </div>

        <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
          <p className="text-sm font-bold text-slate-700">"{subtask.text}"</p>
          <p className="text-[10px] text-slate-400 mt-2 uppercase tracking-wide font-bold">Origin: {parentTask.title}</p>
        </div>

        {hasPartners && (
          <label className="flex items-start gap-3 p-4 rounded-xl border border-blue-100 bg-blue-50/50 cursor-pointer hover:bg-blue-50 transition-colors">
            <input
              type="checkbox"
              checked={keepPartners}
              onChange={(e) => setKeepPartners(e.target.checked)}
              className="mt-1 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <div className="flex-1">
              <span className="text-sm font-bold text-slate-800 block">Inherit Accountability</span>
              <span className="text-xs text-slate-500 block mt-0.5">
                Automatically link {parentTask.partners.length} existing partner{parentTask.partners.length > 1 ? 's' : ''} to this new mission.
              </span>
            </div>
            <Users size={16} className="text-blue-400 mt-1" />
          </label>
        )}

        <div className="flex gap-3 pt-2">
          <button
            onClick={onClose}
            disabled={isPromoting}
            className="flex-1 py-3 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-xl transition-colors uppercase tracking-wider disabled:opacity-50"
          >
            Abort
          </button>
          <button
            onClick={handleConfirm}
            disabled={isPromoting}
            className="flex-[2] py-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-black rounded-xl shadow-lg shadow-blue-600/20 transition-all uppercase tracking-wider active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isPromoting && <Loader2 size={14} className="animate-spin" />}
            {isPromoting ? 'Promoting...' : 'Confirm Promotion'}
          </button>
        </div>
      </div>
    </div>
  );
};

const AppLayout: React.FC = () => {
  // ... (AppLayout implementation remains the same)
  // Re-pasting just to ensure file completeness in output if needed, but for brevity assuming wrapped logic works:
  // Since I need to wrap AppLayout in the Provider, I will modify the `App` component below.

  const [user, setUser] = useState<User | null>(null);
  const [view, setView] = useState<'landing' | 'auth' | 'app'>('landing');
  const [authInitialMode, setAuthInitialMode] = useState<'login' | 'signup'>('login');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | undefined>(undefined);
  const [initialQuadrant, setInitialQuadrant] = useState<Quadrant>(Quadrant.DO);
  const [searchQuery, setSearchQuery] = useState("");
  const [promotionState, setPromotionState] = useState<{ parentTask: Task; subtaskId: string } | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');

  const lastNudgeCheck = useRef<number>(0);
  const location = useLocation();
  const navigate = useNavigate();

  const isPremium = user?.tier === UserTier.PREMIUM || user?.role === UserRole.ADMIN;

  const handleUpgradeRequest = (feature: string) => {
    setUpgradeFeature(feature);
    setShowUpgradeModal(true);
  };

  const handleUpgrade = async (plan: 'monthly' | 'yearly' = 'yearly') => {
    if (!user) return;
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ userId: user.id, userEmail: user.email, plan })
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        throw new Error(data.error || 'Failed to create checkout session');
      }
      window.location.href = data.url;
    } catch (e: any) {
      console.error('[Vanguard] Stripe checkout failed:', e);
      throw e; // Re-throw so UpgradeModal can reset loading state
    }
  };

  const isPublicRoute = useMemo(() => {
    const targets = ['trust', 'partner-action', 'login'];
    return targets.some(target => location.hash.includes(target) || location.pathname.includes(target));
  }, [location]);

  const shouldHideSidebar = useMemo(() => {
    const targets = ['partner-action'];
    return targets.some(target => location.hash.includes(target) || location.pathname.includes(target));
  }, [location]);

  // ... (useEffect logic for routing/theme/data fetching/nudges/infrastructure seeding/login/logout/deleteAccount) ...
  // [Assuming all existing AppLayout logic is preserved here]

  useEffect(() => {
    const path = window.location.pathname;
    if ((path.includes('partner-action') || path.includes('trust')) && !window.location.hash) {
      window.location.hash = `#${path}${window.location.search}`;
    }
  }, [location]);

  // Post-Stripe checkout: verify subscription with backend and activate premium
  const [upgradeProcessing, setUpgradeProcessing] = useState(false);
  const upgradeCheckRef = useRef(false);
  useEffect(() => {
    const hash = window.location.hash || '';
    const search = window.location.search || '';
    const routerSearch = location.search || '';
    const hasUpgradeFlag = hash.includes('upgrade=success') || search.includes('upgrade=success') || routerSearch.includes('upgrade=success');
    if (!user || !hasUpgradeFlag || upgradeCheckRef.current) return;
    // Prevent duplicate runs
    upgradeCheckRef.current = true;
    setUpgradeProcessing(true);

    let attempts = 0;
    const maxAttempts = 8;
    const pollInterval = 3000;

    // First attempt immediately, then poll
    const verifyAndActivate = async () => {
      attempts++;
      try {
        const authHeaders = await getAuthHeaders();
        const res = await fetch(`${API_BASE}/api/stripe/verify-checkout`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ userId: user.id }),
        });
        const result = await res.json();

        if (result.status === 'active') {
          const freshUser = await firebaseService.getUser(user.id);
          if (freshUser) {
            localStorage.setItem('priori_user_session', safeJsonStringify(freshUser));
            setUser(freshUser);
          }
          setUpgradeProcessing(false);
          console.log('[Vanguard] Premium activated after', attempts, 'checks');
          window.location.hash = '#/settings';
          return true;
        }
      } catch (e) {
        console.error('[Vanguard] Verify checkout failed:', e);
      }
      return false;
    };

    // Try immediately
    verifyAndActivate().then(done => {
      if (done) return;
      // Poll if first attempt didn't confirm
      const timer = setInterval(async () => {
        const success = await verifyAndActivate();
        if (success || attempts >= maxAttempts) {
          clearInterval(timer);
          if (!success) {
            setUpgradeProcessing(false);
            console.log('[Vanguard] Premium verification timed out');
            window.location.hash = '#/settings';
          }
        }
      }, pollInterval);
    });
  }, [user?.id, location.search]);

  useEffect(() => {
    if (user?.settings?.theme === 'dark') document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, [user?.settings?.theme]);

  useEffect(() => {
    if (selectedTask && tasks.length > 0) {
      const freshTask = tasks.find(t => t.id === selectedTask.id);
      if (freshTask && freshTask !== selectedTask) {
        setSelectedTask(freshTask);
      }
    }
  }, [tasks, selectedTask]);

  useEffect(() => {
    if (!user) return;
    const unsubBroadcasts = firebaseService.subscribeToBroadcasts(user, (items) => setBroadcasts(items));
    const unsubNotifs = firebaseService.subscribeToUserNotifications(user.id, (items) => setNotifications(items));
    const unsubTasks = firebaseService.subscribeToTasks(user.id, (items) => setTasks(items));
    const unsubCategories = firebaseService.subscribeToCategories(user.id, (items) => setCategories(items));
    return () => { unsubBroadcasts(); unsubNotifs(); unsubTasks(); unsubCategories(); };
  }, [user]);

  useEffect(() => {
    const processNudges = async () => {
      if (!user || tasks.length === 0) return;
      const now = Date.now();
      if (now - lastNudgeCheck.current < 60000) return;
      lastNudgeCheck.current = now;

      const activeNudge = nudgeService.detectActiveNudges(tasks);
      if (activeNudge) {
        const task = tasks.find(t => t.id === activeNudge.taskId);
        const lastNudge = task?.lastNudgedAt || 0;
        
        if (Date.now() - lastNudge > 24 * 60 * 60 * 1000) {
          const event: SystemEvent = activeNudge.type === 'URGENT' ? 'nudge_urgent' : 'nudge_strategic';
          
          // 1. Mobile Push Alert
          await notificationService.dispatchAlert(
            user,
            event,
            activeNudge.type === 'URGENT' ? 'Critical Stagnation' : 'Strategic Drift',
            activeNudge.message
          );

          // 2. Email Orchestration
          communicationService.processEvent({
            to: user.email,
            event: event,
            context: {
              userName: user.displayName,
              userEmail: user.email,
              userId: user.id,
              taskTitle: task?.title || 'Active Mission',
              taskId: task?.id || 'N/A',
              quadrant: task?.currentQuadrant || 'N/A',
              appUrl: window.location.origin,
              timestamp: new Date().toLocaleString()
            }
          }).catch(err => console.error(`[Vanguard] ${event} email failed:`, err));

          if (task) {
            await firebaseService.updateTask(task.id, { lastNudgedAt: Date.now() });
          }
        }
      }

      const lastPulseKey = `vanguard_last_pulse_sync_${user.id}`;
      const lastPulse = localStorage.getItem(lastPulseKey);
      const dayMs = 24 * 60 * 60 * 1000;

      if (!lastPulse || (now - parseInt(lastPulse)) > dayMs) {
        const pulseSummary = nudgeService.getDailyPulseSummary(tasks);
        if (pulseSummary) {
          localStorage.setItem(lastPulseKey, now.toString());
          // Dispatch local push notification only
          notificationService.dispatchAlert(user, 'daily', 'Daily Mission Brief', pulseSummary.message);
          console.log("[Vanguard] Daily local pulse dispatched. Server-side relay handled by Command Hub.");
        }
      }
    };
    processNudges();
  }, [tasks, user]);

  useEffect(() => {
    if (!user || broadcasts.length === 0) return;
    const latest = broadcasts[0];
    const isUnread = !notifications.some(n => n.broadcastId === latest.id);
    if (latest.priority === 'CRITICAL' && isUnread && latest.targetGroup === 'INDIVIDUAL') {
      notificationService.dispatchAlert(user, 'partner_request', latest.title, latest.message);
    }
  }, [broadcasts, notifications, user]);

  const ensureInfrastructureSeeded = async () => {
    try {
      const associations = await firebaseService.getAssociations();
      if (associations.length > 0) { localStorage.setItem('vanguard_infra_provisioned', 'true'); return; }
      await firebaseService.seedData();
      localStorage.setItem('vanguard_infra_provisioned', 'true');
    } catch (e) { }
  };

  useEffect(() => {
    ensureInfrastructureSeeded().catch(() => {});

    const unsubAuth = firebaseService.onAuthChange(async (firebaseUser) => {
      try {
        if (firebaseUser) {
          // Firebase Auth user is signed in — load from Firestore
          const userData = await firebaseService.getUser(firebaseUser.uid);
          if (userData) {
            // Premium Expiry Check
            if (userData.tier === UserTier.PREMIUM && userData.premiumExpiry && userData.premiumExpiry < Date.now()) {
              console.log('[Vanguard] Premium expired, downgrading user');
              userData.tier = UserTier.FREE;
              userData.subscriptionStatus = 'EXPIRED';
              await firebaseService.updateUser(userData.id, { tier: UserTier.FREE, subscriptionStatus: 'EXPIRED' });
            }

            // Timezone Sync Logic
            const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const browserOffset = new Date().getTimezoneOffset();
            if (userData.timezone !== browserTimezone || userData.timezoneOffset !== browserOffset) {
              await firebaseService.updateUser(userData.id, { timezone: browserTimezone, timezoneOffset: browserOffset });
              userData.timezone = browserTimezone;
              userData.timezoneOffset = browserOffset;
            }

            localStorage.setItem('priori_user_session', safeJsonStringify(userData));
            setUser(userData);
            setView('app');
          }
        } else {
          // No Firebase Auth session — check localStorage for backward compat during migration
          const savedUser = localStorage.getItem('priori_user_session');
          if (savedUser) {
            try {
              const parsedUser: User = JSON.parse(savedUser);
              setUser(parsedUser);
              setView('app');
            } catch (e) {
              localStorage.removeItem('priori_user_session');
            }
          }
        }
      } catch (err) {
        console.error('[Vanguard] Auth state error:', err);
      } finally {
        setIsLoading(false);
      }
    });

    return () => unsubAuth();
  }, []);

  const handleLogin = async (loggedUser: User, isNewUser: boolean = false) => {
    setIsLoading(true);

    // Detect and save timezone on login
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const browserOffset = new Date().getTimezoneOffset();
    const userToSave = { ...loggedUser, timezone: browserTimezone, timezoneOffset: browserOffset };

    await firebaseService.updateUser(loggedUser.id, { timezone: browserTimezone, timezoneOffset: browserOffset });

    localStorage.setItem('priori_user_session', safeJsonStringify(userToSave));
    setUser(userToSave);
    setView('app');
    navigate('/');
    if (isNewUser) {
      communicationService.processEvent({
        to: loggedUser.email,
        event: 'welcome_new_user',
        context: { userId: loggedUser.id, userEmail: loggedUser.email, userName: loggedUser.displayName, displayName: loggedUser.displayName, appUrl: window.location.origin }
      }).catch(() => { });
    }
    setIsLoading(false);
    window.location.hash = '#/';
  };

  const handleLogout = async () => {
    await firebaseService.logout();
    localStorage.removeItem('priori_user_session');
    localStorage.removeItem('vanguard_infra_provisioned');
    firebaseService.clearLocalData();
    setUser(null);
    setTasks([]);
    setView('landing');
    setIsMobileMenuOpen(false);
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      await communicationService.processEvent({ to: user.email, event: 'profile_deleted', context: { userEmail: user.email, userName: user.displayName, appUrl: window.location.origin } }).catch(() => { });
      await firebaseService.deleteUser(user.id);
      handleLogout();
    } catch (e) { alert("System could not decommission profile."); } finally { setIsLoading(false); }
  };

  const handlePromoteSubtaskTrigger = (parentTask: Task, subtaskId: string) => {
    setPromotionState({ parentTask, subtaskId });
  };

  const executePromotion = async (keepPartners: boolean) => {
    if (!user || !promotionState) return;
    const { parentTask, subtaskId } = promotionState;
    // Immediately clear promotion state to prevent duplicate calls
    setPromotionState(null);
    setIsLoading(true);
    try {
      await firebaseService.promoteSubtask(parentTask, subtaskId, user, { inheritPartners: keepPartners });
      notificationService.sendLocal("Tactical Promotion", "Objective promoted.");
    } catch (e) { alert("Failed to promote subtask."); } finally { setIsLoading(false); }
  };

  const handleNavigateToTask = (taskId: string) => {
    setIsTaskModalOpen(false);
    const target = tasks.find(t => t.id === taskId);
    if (target) {
      setTimeout(() => { setSelectedTask(target); setIsTaskModalOpen(true); }, 150);
    } else { alert("Target mission objective not found in current sector."); }
  };

  const handleSaveTask = async (taskData: Partial<Task>) => {
    if (!user) return;
    setIsLoading(true);
    const targetTaskId = taskData.id || selectedTask?.id;
    const existingTask = targetTaskId ? tasks.find(t => t.id === targetTaskId) : null;

    try {
      if (existingTask) {
        // ... (Task update logic logic)
        let subtasks = taskData.subtasks || existingTask.subtasks || [];
        let newStatus: TaskStatus = taskData.status || existingTask.status;
        let isCompleted = taskData.isCompleted ?? existingTask.isCompleted;

        if (newStatus === 'ARCHIVED') isCompleted = true;
        else if (taskData.isCompleted === true || taskData.status === 'COMPLETED') {
          newStatus = 'COMPLETED';
          isCompleted = true;
          subtasks = subtasks.map(s => ({ ...s, completed: true }));
        }
        else if (existingTask.isCompleted && taskData.isCompleted === false) {
          // Un-completing a task: restore subtask states from before force-completion
          // Use the incoming subtask data (which preserves per-subtask state from the modal)
          // Don't force all subtasks to stay completed
          isCompleted = false;
          const hasProgress = subtasks.some(s => s.completed);
          newStatus = hasProgress ? 'IN_PROGRESS' : 'NEW';
        }
        else if (subtasks.length > 0) {
          const total = subtasks.length;
          const completed = subtasks.filter(s => s.completed).length;
          if (completed === total) { newStatus = 'COMPLETED'; isCompleted = true; }
          else if (completed > 0) { newStatus = 'IN_PROGRESS'; isCompleted = false; }
          else { if (newStatus !== 'IN_PROGRESS') newStatus = 'NEW'; isCompleted = false; }
        }
        else if ((newStatus as string) === 'IN_PROGRESS' || (newStatus as string) === 'NEW') isCompleted = false;

        // Track quadrant movement history
        let quadrantHistory = existingTask.quadrantHistory || [];
        if (taskData.currentQuadrant && taskData.currentQuadrant !== existingTask.currentQuadrant) {
          quadrantHistory = [...quadrantHistory, {
            fromQuadrant: existingTask.currentQuadrant,
            toQuadrant: taskData.currentQuadrant,
            movedAt: Date.now()
          }];
        }

        const updates: Partial<Task> = {
          ...taskData,
          status: newStatus,
          isCompleted: isCompleted,
          completedAt: isCompleted ? (existingTask.completedAt || Date.now()) : null,
          updatedAt: Date.now(),
          subtasks,
          subtaskCount: subtasks.length,
          completedSubtaskCount: subtasks.filter(s => s.completed).length,
          hasSubtasks: subtasks.length > 0,
          quadrantHistory
        };
        await firebaseService.updateTask(targetTaskId!, updates);

        // SUBTASK COMPLETION TRIGGER (Stabilization Fix)
        // Relaxed guard to allow notifications even if task is completed (for granular subtask feedback)
        if (taskData.subtasks) {
          const newlyCompleted = (taskData.subtasks as SubTask[]).filter(s => {
            const old = existingTask.subtasks?.find(os => os.id === s.id);
            return s.completed && (!old || !old.completed);
          });
          newlyCompleted.forEach(s => {
            console.log(`[Vanguard] Triggering Partner Notification for Subtask: ${s.text}`);
            communicationService.notifyPartners({ ...existingTask, ...updates } as Task, 'subtask', user.displayName, user.email, s as any).catch(() => { });
          });
        }

        if (isCompleted && newStatus !== 'ARCHIVED' && !existingTask.isCompleted) {
          communicationService.notifyPartners({ ...existingTask, ...updates } as Task, 'completed', user.displayName, user.email).catch(() => { });
          notificationService.dispatchAlert(user, 'completed', 'Objective Secured', `Mission "${existingTask.title}" secured.`);

          // PHASE 10: Promoted Task Sync Logic
          if (existingTask.originType === 'promoted_subtask' && existingTask.parentTaskId && existingTask.parentSubtaskId) {
            console.log(`[Vanguard] Promoted Task Completed. Syncing back to Parent: ${existingTask.parentTaskId}`);
            firebaseService.syncParentSubtaskCompletion(
              existingTask.parentTaskId,
              existingTask.parentSubtaskId,
              true
            ).catch(err => console.error(`[Vanguard] Parent Sync Failed:`, err));
          }
        }
      } else {
        // Create Logic
        const id = Math.random().toString(36).substr(2, 9);
        const subtasks = taskData.subtasks || [];
        const newTask: Task = {
          id, taskId: id, userId: user.id,
          title: taskData.title || 'New Objective',
          description: taskData.description || null,
          initialQuadrant: taskData.currentQuadrant || Quadrant.DO,
          currentQuadrant: taskData.currentQuadrant || Quadrant.DO,
          quadrantHistory: [],
          status: 'NEW', isCompleted: false,
          hasSubtasks: subtasks.length > 0,
          subtaskCount: subtasks.length,
          completedSubtaskCount: subtasks.filter(s => s.completed).length,
          subtasks,
          createdAt: Date.now(), updatedAt: Date.now(), completedAt: null,
          dueDate: taskData.dueDate,
          category: taskData.category || 'primary_sector', tags: [], source: 'USER_CREATED',
          partners: taskData.partners || []
        };
        await firebaseService.createTask(newTask);
        
        // Trigger Orchestration: Task Created
        communicationService.processEvent({
          to: user.email,
          event: 'created',
          context: {
            userId: user.id,
            userEmail: user.email,
            userName: user.displayName,
            taskId: newTask.id,
            taskTitle: newTask.title,
            quadrant: newTask.currentQuadrant,
            appUrl: window.location.origin,
            timestamp: new Date().toLocaleString()
          }
        }).catch(err => console.error("[Vanguard] Created trigger failed:", err));
      }
    } catch (err) { console.error("Mission Deployment Failed:", err); } finally { setIsTaskModalOpen(false); setSelectedTask(undefined); setIsLoading(false); }
  };

  const markRead = async (broadcastId: string) => {
    if (!user) return;
    await firebaseService.markNotificationRead(user.id, broadcastId);
  };

  const unreadCount = broadcasts.filter(b => !notifications.some(n => n.broadcastId === b.id)).length;

  return (
    <div className="flex min-h-screen bg-vanguard-bg text-vanguard-text font-sans transition-colors duration-500">
      {/* ... (Sidebar and Header UI reused) */}
      {!shouldHideSidebar && (
        <aside className="hidden lg:flex flex-col w-72 border-r border-vanguard-border p-8 bg-vanguard-surface shadow-sm">
          <div className="flex items-center gap-4 mb-16">
            <div className="w-12 h-12 bg-[#0f172a] rounded-2xl flex items-center justify-center font-black text-white text-xl shadow-lg">P</div>
            <div className="flex flex-col">
              <span className="text-xl font-bold tracking-tight text-vanguard-text">Priori.ai</span>
              <span className="text-[9px] font-bold text-blue-600 tracking-[0.2em] uppercase mt-1">Vanguard</span>
            </div>
          </div>
          <nav className="flex-1 space-y-4"><NavLinks user={user} isPremium={isPremium} /></nav>
          <div className="mt-auto pt-8 border-t border-vanguard-border space-y-8">
            {user ? (
              <div className="space-y-8">
                <div className="flex items-center gap-4 px-2">
                  <div className={`w-14 h-14 rounded-2xl bg-vanguard-bg border border-vanguard-border flex items-center justify-center text-blue-600 ${user?.tier === UserTier.PREMIUM ? 'ring-2 ring-blue-500 shadow-lg' : ''}`}><UserIcon size={24} /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-vanguard-text truncate">{user?.displayName}</p>
                    <p className={`text-[9px] font-bold uppercase tracking-[0.2em] ${user?.tier === UserTier.PREMIUM ? 'text-blue-600' : 'text-vanguard-muted'}`}>{user?.tier}</p>
                  </div>
                </div>
                <button onClick={handleLogout} className="w-full flex items-center justify-center gap-3 px-6 py-4 text-[10px] font-bold text-vanguard-muted hover:text-rose-500 transition-all bg-vanguard-bg rounded-2xl border border-vanguard-border uppercase tracking-[0.2em] shadow-sm"><LogOut size={14} /> Log Out</button>
              </div>
            ) : (
              <button onClick={() => { setAuthInitialMode('login'); navigate('/login'); }} className="w-full flex items-center justify-center gap-3 px-6 py-4 text-[10px] font-bold text-blue-600 hover:bg-blue-50 transition-all bg-vanguard-bg rounded-2xl border border-blue-100 uppercase tracking-[0.2em] shadow-sm"><LogIn size={14} /> Log In</button>
            )}
          </div>
        </aside>
      )}

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[100] flex lg:hidden animate-in fade-in duration-300">
          <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="relative w-80 bg-vanguard-surface h-full shadow-2xl flex flex-col p-8 animate-in slide-in-from-left duration-500 border-r border-vanguard-border">
            {/* Mobile Menu Content */}
            <div className="flex justify-between items-center mb-12">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#0f172a] rounded-xl flex items-center justify-center font-black text-white">P</div>
                <span className="font-bold">Priori</span>
              </div>
              <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 hover:bg-black/5 rounded-xl transition-all"><X size={24} /></button>
            </div>
            <nav className="flex-1 space-y-4"><NavLinks user={user} isPremium={isPremium} /></nav>
            <div className="mt-auto pt-8 border-t border-vanguard-border">
              {user ? <button onClick={handleLogout} className="w-full flex items-center justify-center gap-3 px-6 py-4 text-[10px] font-bold text-vanguard-muted hover:text-rose-500 transition-all bg-vanguard-bg rounded-2xl border border-vanguard-border uppercase tracking-[0.2em]"><LogOut size={14} /> Log Out</button> : <button onClick={() => { setAuthInitialMode('login'); navigate('/login'); }} className="w-full flex items-center justify-center gap-3 px-6 py-4 text-[10px] font-bold text-blue-600 bg-vanguard-bg rounded-2xl border border-blue-100 uppercase tracking-[0.2em]"><LogIn size={14} /> Log In</button>}
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Header */}
        <header className="h-24 border-b border-vanguard-border px-6 lg:px-12 flex items-center justify-between bg-vanguard-surface/80 backdrop-blur-md z-20">
          <div className="flex items-center gap-4">
            <button onClick={() => setIsMobileMenuOpen(true)} className="lg:hidden p-3 bg-vanguard-bg border border-vanguard-border rounded-xl text-vanguard-text transition-all hover:bg-black/5 active:scale-95"><Menu size={20} /></button>
            <div className="lg:hidden flex items-center gap-3">
              <div className="w-8 h-8 bg-[#0f172a] rounded-lg flex items-center justify-center font-black text-white text-sm">P</div>
              <span className="font-bold text-sm tracking-tight">Priori</span>
            </div>
            {user && (
              <div className="hidden md:flex relative group w-96 ml-4">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-vanguard-muted group-focus-within:text-blue-500 transition-colors" size={18} />
                <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search active missions..." className="w-full bg-vanguard-bg border border-vanguard-border rounded-2xl py-3 pl-14 pr-6 text-sm text-vanguard-text focus:outline-none focus:border-blue-500 transition-all font-medium" />
              </div>
            )}
          </div>
          <div className="flex items-center gap-4 lg:gap-8">
            {user ? (
              <div className="flex items-center gap-4 lg:gap-8">
                <div className="relative">
                  <button onClick={() => setIsNotifOpen(!isNotifOpen)} className={`p-3 rounded-2xl border border-vanguard-border text-vanguard-text transition-all relative ${unreadCount > 0 ? 'bg-blue-600/5 border-blue-600/20' : 'hover:bg-black/5'}`}>
                    <Bell size={20} className={unreadCount > 0 ? 'text-blue-600 animate-pulse' : ''} />
                    {unreadCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white rounded-full text-[9px] font-black flex items-center justify-center border-2 border-vanguard-surface shadow-lg">{unreadCount}</span>}
                  </button>
                  {isNotifOpen && (
                    <div className="absolute right-0 mt-4 w-96 max-w-[calc(100vw-2rem)] bg-vanguard-surface border border-vanguard-border rounded-[2.5rem] shadow-2xl p-6 space-y-6 z-[100] animate-in slide-in-from-top-4 duration-300">
                      <div className="flex justify-between items-center border-b border-vanguard-border pb-4">
                        <h4 className="text-xs font-black text-vanguard-text uppercase tracking-widest flex items-center gap-2"><Zap className="text-blue-600" size={14} /> Tactical Broadcasts</h4>
                        <button onClick={() => setIsNotifOpen(false)}><X size={16} /></button>
                      </div>
                      <div className="max-h-[400px] overflow-y-auto custom-scrollbar space-y-4">
                        {broadcasts.length === 0 ? <div className="py-12 text-center opacity-30 italic text-[10px] font-bold uppercase">No active broadcasts</div> : broadcasts.map(b => {
                          const isRead = notifications.some(n => n.broadcastId === b.id);
                          const hasCta = b.ctaUrl && b.ctaUrl.trim().length > 0;
                          return (
                            <div key={b.id} className={`p-5 rounded-[1.75rem] border transition-all ${isRead ? 'bg-vanguard-bg border-vanguard-border opacity-60' : 'bg-blue-500/5 border-blue-500/20 shadow-sm scale-[1.02]'}`}>
                              <div className="flex justify-between items-start mb-2">
                                <h5 className="text-[11px] font-black text-vanguard-text uppercase tracking-tight flex items-center gap-2 flex-1 pr-2">{b.priority === 'CRITICAL' ? <ShieldIcon size={12} className="text-rose-500 animate-pulse" /> : null} {b.title}</h5>
                                <span className="text-[8px] font-mono font-bold text-vanguard-muted whitespace-nowrap">{new Date(b.timestamp).toLocaleDateString()}</span>
                              </div>
                              <p className="text-[10px] text-vanguard-muted leading-relaxed font-medium mb-3">{b.message}</p>
                              {hasCta && <a href={b.ctaUrl!.startsWith('http') ? b.ctaUrl : `https://${b.ctaUrl}`} target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-2 py-3.5 mb-3 bg-[#0f172a] text-white rounded-xl text-[9px] font-black uppercase tracking-[0.1em] hover:bg-black transition-all shadow-lg active:scale-[0.98] custom-cta-link"><ExternalLink size={12} /> {b.ctaLabel || 'TAKE ACTION'}</a>}
                              {!isRead && <button onClick={() => markRead(b.id)} className="text-[9px] font-black text-blue-600 uppercase tracking-widest hover:underline flex items-center gap-1.5"><CheckCircle2 size={10} /> Mark as Read</button>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => { setInitialQuadrant(Quadrant.DO); setIsTaskModalOpen(true); }}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl shadow-lg flex items-center justify-center gap-2 uppercase tracking-widest active:scale-95 transition-all"
                >
                  <Command size={16} /> <span>New Mission</span>
                </button>
              </div>
            ) : null}
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-6 lg:px-12 py-10 custom-scrollbar relative z-10">
          <Routes>
            <Route path="/login" element={
              user ? <Navigate to="/" /> : <AuthPage onLogin={handleLogin} initialMode={authInitialMode} />
            } />
            <Route path="/" element={
              user ? (
                <EisenhowerMatrix
                  tasks={tasks.filter(t => t.title.toLowerCase().includes(searchQuery.toLowerCase()))}
                  categories={categories}
                  onAddTask={(q) => { setInitialQuadrant(q); setSelectedTask(undefined); setIsTaskModalOpen(true); }}
                  onToggleTask={(id) => {
                    const task = tasks.find(t => t.id === id);
                    if (task) {
                      const nextCompleted = !task.isCompleted;
                      let nextStatus: TaskStatus;
                      if (nextCompleted) {
                        nextStatus = 'COMPLETED';
                      } else {
                        // Restore appropriate status: IN_PROGRESS if subtasks have progress, else NEW
                        const hasSubtaskProgress = (task.subtasks || []).some(s => s.completed);
                        nextStatus = hasSubtaskProgress ? 'IN_PROGRESS' : 'NEW';
                      }
                      handleSaveTask({ id: task.id, isCompleted: nextCompleted, status: nextStatus });
                    }
                  }}
                  onArchiveTask={(id) => { handleSaveTask({ id, status: 'ARCHIVED' }); }}
                  onSelectTask={(task) => { setSelectedTask(task); setIsTaskModalOpen(true); }}
                  onMoveTask={(id, q) => { const task = tasks.find(t => t.id === id); if (task) handleSaveTask({ id: task.id, currentQuadrant: q }); }}
                  isPremium={isPremium}
                  onUpgradeRequest={handleUpgradeRequest}
                />
              ) : (
                <LandingPage onGetStarted={(mode) => {
                  if (mode) setAuthInitialMode(mode);
                  navigate('/login');
                }} />
              )
            } />
            <Route path="/registry" element={user ? <TaskLogPage tasks={tasks} categories={categories} onSelectTask={(task) => { setSelectedTask(task); setIsTaskModalOpen(true); }} /> : <Navigate to="/login" />} />
            <Route path="/strategy" element={user ? (
              isPremium ? <AnalyticsDashboard tasks={tasks} categories={categories} /> : (
                <div className="max-w-2xl mx-auto text-center py-20 space-y-8 animate-in fade-in">
                  <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-3xl flex items-center justify-center mx-auto">
                    <BarChart3 size={36} />
                  </div>
                  <div className="space-y-3">
                    <h2 className="text-3xl font-bold text-slate-900 tracking-tight">Strategy & Performance</h2>
                    <p className="text-slate-500 text-sm leading-relaxed max-w-md mx-auto">Unlock AI-powered insights, completion metrics, strategic focus tracking, and 30-day trend analysis with Premium.</p>
                  </div>
                  <button
                    onClick={() => handleUpgradeRequest('Strategy & Performance Dashboard')}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm rounded-xl transition-all shadow-lg shadow-blue-500/25"
                  >
                    <Zap size={16} fill="currentColor" /> Upgrade to Unlock
                  </button>
                </div>
              )
            ) : <Navigate to="/login" />} />
            <Route path="/trust" element={<TrustCenter />} />
            <Route path="/partner-action" element={<PartnerActionRelay />} />
            <Route path="/settings" element={user ? <SettingsPage user={user} onUserUpdate={(u) => setUser(u)} onDeleteAccount={handleDeleteAccount} categories={categories} onCategoriesUpdate={(cats) => setCategories(cats)} /> : <Navigate to="/login" />} />
            <Route path="/admin" element={user?.role === UserRole.ADMIN ? <IntelPage /> : user ? <Navigate to="/" /> : <Navigate to="/login" />} />
          </Routes>
        </div>

        {isTaskModalOpen && user && (
          <TaskModal
            task={selectedTask}
            user={user}
            onClose={() => { setIsTaskModalOpen(false); setSelectedTask(undefined); }}
            onSave={handleSaveTask}
            initialQuadrant={initialQuadrant}
            categories={categories}
            onCategoryCreated={(cat) => setCategories(prev => [...prev, cat])}
            isPremium={isPremium}
            onUpgrade={handleUpgrade}
            onPromoteSubtask={handlePromoteSubtaskTrigger}
            onNavigateToTask={handleNavigateToTask}
          />
        )}

        {promotionState && user && (
          <PromotionDialog
            isOpen={!!promotionState}
            parentTask={promotionState.parentTask}
            subtask={promotionState.parentTask.subtasks.find(s => s.id === promotionState.subtaskId) || null}
            onClose={() => setPromotionState(null)}
            onConfirm={executePromotion}
          />
        )}

        {user && !isPublicRoute && (
          <AIAgentOverlay user={user} onCommand={(cmd) => {
            if (cmd.toLowerCase().startsWith('create ')) {
              const title = cmd.substring(7);
              handleSaveTask({ title, currentQuadrant: Quadrant.DO });
            }
          }} />
        )}

        {upgradeProcessing && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-indigo-600 text-white px-6 py-3 rounded-2xl shadow-lg flex items-center gap-3 animate-in slide-in-from-bottom-4">
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            <span className="text-sm font-bold">Activating your premium subscription — this may take up to 30 seconds...</span>
          </div>
        )}

        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          onUpgrade={handleUpgrade}
          feature={upgradeFeature}
        />
      </main>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <HashRouter>
      <ActionFlowProvider>
        <ConfirmationProvider>
          <AppLayout />
        </ConfirmationProvider>
      </ActionFlowProvider>
    </HashRouter>
  );
};

export default App;
